import "./css/variables.css";
import "./App.css";
import React, { Suspense } from 'react';
import SideBar from "./components/Sidebar/SideBar";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import AgentReconnectBanner from "./components/AgentReconnectBanner";
import CallbackAutoBanner from "./components/CallbackAutoBanner";

// Lazy-loaded pages — each becomes a separate chunk, loaded on first visit
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Users = React.lazy(() => import("./pages/Users"));
const TeamLeaders = React.lazy(() => import("./pages/TeamLeaders"));
const VoiceBot = React.lazy(() => import("./pages/VoiceBot"));
const VoiceBotIntent = React.lazy(() => import("./pages/VoiceBotIntent"));
const VoiceBotKnowledgeBase = React.lazy(() => import("./pages/VoiceBotKnowledgeBase"));
const VoiceBotTools = React.lazy(() => import("./pages/VoiceBotTools"));
const VoiceBotCallLogs = React.lazy(() => import("./pages/VoiceBotCallLogs"));
const VoiceBotSettings = React.lazy(() => import("./pages/VoiceBotSettings"));
const MessagingSettings = React.lazy(() => import("./pages/MessagingSettings"));
const Messaging = React.lazy(() => import("./pages/Messaging"));
const VoiceBotConversions = React.lazy(() => import("./pages/VoiceBotConversions"));
const Messages = React.lazy(() => import("./pages/Messages"));
const FileManager = React.lazy(() => import("./pages/FileManager"));
const Analytics = React.lazy(() => import("./pages/Analytics"));
const Order = React.lazy(() => import("./pages/Order"));
const Saved = React.lazy(() => import("./pages/Saved"));
const Setting = React.lazy(() => import("./pages/Setting"));
const ChannelSetting = React.lazy(() => import("./pages/ChannelSetting"));
const TrunkSettings = React.lazy(() => import("./pages/TrunkSettings"));
const AddCampaign = React.lazy(() => import("./pages/AddCampaign"));
const CampDid = React.lazy(() => import("./pages/CampDid"));
const Callbackactive = React.lazy(() => import("./pages/Callbackactive"));
const Report = React.lazy(() => import("./pages/Report"));
const UploadAudio = React.lazy(() => import("./pages/UploadAudio"));
const MusicOnHold = React.lazy(() => import("./pages/MusicOnHold"));
const FilterReport = React.lazy(() => import("./pages/FilterReport"));
const ChurnLeads = React.lazy(() => import("./pages/ChurnLeads"));
const Agentlog = React.lazy(() => import("./pages/Agentlog"));
const UploadNumber = React.lazy(() => import("./pages/UploadNumber"));
const AssignDid = React.lazy(() => import("./pages/AssignDid"));
const Editgui = React.lazy(() => import("./pages/Editgui"));
const Calllog = React.lazy(() => import("./pages/Calllog"));
const Makegui = React.lazy(() => import("./pages/Makegui"));
const IvrReport = React.lazy(() => import("./pages/IvrReport"));
const Changepwd = React.lazy(() => import("./pages/Changepwd"));
const Ivr = React.lazy(() => import("./pages/Ivr"));
const Crm = React.lazy(() => import("./pages/Crm"));
const Realtime = React.lazy(() => import("./pages/Realtime"));
const Assignagent = React.lazy(() => import("./pages/Assignagent"));
const Assignlist = React.lazy(() => import("./pages/Assignlist"));
const Audio = React.lazy(() => import("./pages/Audio"));
const Group = React.lazy(() => import("./pages/Group"));
const IvrGroupAssignAgent = React.lazy(() => import("./pages/IvrGroupAssignAgent"));
const Agent = React.lazy(() => import("./pages/Agent"));
const Break = React.lazy(() => import("./pages/Break"));
const Callback = React.lazy(() => import("./pages/Callback"));
const PreviewDialer = React.lazy(() => import("./pages/PreviewDialer"));
const AddDisposition = React.lazy(() => import("./pages/AddDisposition"));
const Agentcalllog = React.lazy(() => import("./pages/Agentcalllog"));
const AgentApr = React.lazy(() => import("./pages/AgentApr"));
const Callhistory = React.lazy(() => import("./pages/Callhistory"));
const AgentPerformance = React.lazy(() => import("./pages/AgentPerformance"));
const DrawIvr = React.lazy(() => import("./pages/DrawIvr"));
const IvrTab = React.lazy(() => import("./pages/IvrTab"));
const IvrSmsReport = React.lazy(() => import("./pages/IvrSmsReport"));
const AddAgent = React.lazy(() => import("./pages/AddAgent"));
const Reportlive = React.lazy(() => import("./pages/Reportlive"));
const AllowIP = React.lazy(() => import("./pages/AllowIP"));
const AllowPort = React.lazy(() => import("./pages/AllowPort"));
const Fail2ban = React.lazy(() => import("./pages/Fail2ban"));
const NetworkMonitor = React.lazy(() => import("./pages/NetworkMonitor"));
const DncSettings = React.lazy(() => import("./pages/DncSettings"));
const PjsipEndpoints = React.lazy(() => import("./pages/PjsipEndpoints"));
const AddObdCampaign = React.lazy(() => import("./pages/obd/AddObdCampaign"));
const ObdRecording = React.lazy(() => import("./pages/obd/ObdRecording"));
const UploadObdNumber = React.lazy(() => import("./pages/obd/UploadObdNumber"));
const AddObdAgent = React.lazy(() => import("./pages/obd/AddObdAgent"));
const ObdChannelSetting = React.lazy(() => import("./pages/obd/ObdChannelSetting"));
const ObdBlockNumber = React.lazy(() => import("./pages/obd/ObdBlockNumber"));
const ObdProfile = React.lazy(() => import("./pages/obd/ObdProfile"));
const ObdReport = React.lazy(() => import("./pages/obd/ObdReport"));
const ObdUserPermissions = React.lazy(() => import("./pages/obd/ObdUserPermissions"));
const ObdDialerSettings = React.lazy(() => import("./pages/ObdDialerSettings"));
const DialerSettings = React.lazy(() => import("./pages/DialerSettings"));
const ObdFlowList = React.lazy(() => import("./pages/obd/ObdFlowList"));
const DrawObdFlow = React.lazy(() => import("./pages/obd/DrawObdFlow"));
const BlockNumber = React.lazy(() => import("./pages/BlockNumber"));
const SmsportSetting = React.lazy(() => import("./pages/SmsportSetting"));
const Smssuits = React.lazy(() => import("./pages/Smssuits"));
const Sharescreen = React.lazy(() => import("./pages/Sharescreen"));
const Callbackall = React.lazy(() => import("./pages/Callbackall"));
const Callbackreport = React.lazy(() => import("./pages/Callbackreport"));
const Callbackreportall = React.lazy(() => import("./pages/Callbackreportall"));
const BreakReport = React.lazy(() => import("./pages/BreakReport"));
const FilterReportold = React.lazy(() => import("./pages/FilterReportold"));
const TransferNumber = React.lazy(() => import("./pages/TransferNumber"));
const AdminPermissions = React.lazy(() => import("./pages/AdminPermissions"));
const CampaignScript = React.lazy(() => import("./pages/CampaignScript"));
const PasswordChangeLogs = React.lazy(() => import("./pages/PasswordChangeLogs"));
const SuperAdminActivityLog = React.lazy(() => import("./pages/SuperAdminActivityLog"));
const Resellers = React.lazy(() => import("./pages/Resellers"));
const MyQuota = React.lazy(() => import("./pages/MyQuota"));
const ChannelStatus = React.lazy(() => import("./pages/ChannelStatus"));
const MyChannelStatusAccess = React.lazy(() => import("./pages/MyChannelStatusAccess"));
const MyResellers = React.lazy(() => import("./pages/MyResellers"));
const MyVoiceBotAccess = React.lazy(() => import("./pages/MyVoiceBotAccess"));
const MyMessagingAccess = React.lazy(() => import("./pages/MyMessagingAccess"));
const MyAdminPermissions = React.lazy(() => import("./pages/MyAdminPermissions"));
const VoiceBotQuotas = React.lazy(() => import("./pages/VoiceBotQuotas"));
const JitsiDiagnostics = React.lazy(() => import("./pages/jitsi/JitsiDiagnostics"));
const JitsiSettings = React.lazy(() => import("./pages/jitsi/JitsiSettings"));
const VideoDashboard = React.lazy(() => import("./pages/jitsi/VideoDashboard"));
const VideoNewMeeting = React.lazy(() => import("./pages/jitsi/VideoNewMeeting"));
const VideoMyRooms = React.lazy(() => import("./pages/jitsi/VideoMyRooms"));
const VideoRoom = React.lazy(() => import("./pages/jitsi/VideoRoom"));
const VideoEscalate = React.lazy(() => import("./pages/jitsi/VideoEscalate"));
const VideoLiveMonitor = React.lazy(() => import("./pages/jitsi/VideoLiveMonitor"));
const VideoRecordings = React.lazy(() => import("./pages/jitsi/VideoRecordings"));
const VoiceBotAccess = React.lazy(() => import("./pages/jitsi/VoiceBotAccess"));
const JitsiWidgetSettings = React.lazy(() => import("./pages/jitsi/JitsiWidgetSettings"));
const Skills = React.lazy(() => import("./pages/Skills"));
const SupervisorSkills = React.lazy(() => import("./pages/SupervisorSkills"));
const GeminiVoiceCatalog = React.lazy(() => import("./pages/GeminiVoiceCatalog"));
const AngularBuildUpload = React.lazy(() => import("./pages/AngularBuildUpload"));
const LiveKitCall = React.lazy(() => import("./pages/livekit/LiveKitCall"));

// Role shorthand constants
// ROLE_OBDADMIN included here so every page the superadmin can grant via
// AdminPermissions actually renders when clicked. Data is still scoped
// per-tenant server-side via effectiveAdminId(user), so even though the
// route allows an OBDADMIN to land on the page, they only see their own
// tenant's records. Sidebar visibility is still controlled by the
// pagePermissions CSV — routes just stop redirecting OBDADMIN away.
const ADMIN = ['ROLE_ADMIN', 'ROLE_TEAMLEADER', 'ROLE_OBDADMIN'];
const SUPER = ['ROLE_SUPERADMIN'];
const ADMIN_SUPER = ['ROLE_ADMIN', 'ROLE_SUPERADMIN', 'ROLE_TEAMLEADER', 'ROLE_OBDADMIN'];
const AGENT_USER = ['ROLE_USER'];
const ALL_ROLES = ['ROLE_ADMIN', 'ROLE_SUPERADMIN', 'ROLE_TEAMLEADER', 'ROLE_USER', 'ROLE_AGENT', 'ROLE_OBDADMIN', 'ROLE_OBDUSER', 'ROLE_RESELLER'];
// RESELLER inherits a SUPERADMIN-shaped slice — user management, channel/DID
// assignment, reports — but capped by the reseller's quota row. Used to
// extend existing ADMIN_SUPER routes without spelling RESELLER out each time.
const SUPER_RESELLER = ['ROLE_SUPERADMIN', 'ROLE_RESELLER'];
// ChannelSetting is also opened up to ROLE_ADMIN (tenant-scoped server-side —
// see AdminPageController.ChannelSettingAction) without widening the other
// SUPER_RESELLER routes (e.g. AssignDid) that weren't asked for.
const SUPER_RESELLER_ADMIN = ['ROLE_SUPERADMIN', 'ROLE_RESELLER', 'ROLE_ADMIN'];

// Loading spinner shown while a lazy chunk is downloading
const PageLoader = () => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '60vh', gap: 10, color: '#64748b', fontSize: '0.85rem',
  }}>
    <div className="spinner-border spinner-border-sm text-primary" role="status" />
    Loading...
  </div>
);

/**
 * Sticky banner shown whenever an impersonation session is active.
 * Reads `originalRoles` / `originalUsername` from localStorage (set by
 * the "View as" action in Users.jsx) and offers an Exit button that
 * calls /Admin/ExitImpersonation — the server swaps the jwt cookie
 * back to the original user, and we restore the localStorage keys.
 */
function ImpersonationBanner() {
  const [origUser, setOrigUser] = React.useState(localStorage.getItem('originalUsername') || '');
  const [origRoles, setOrigRoles] = React.useState(localStorage.getItem('originalRoles') || '');

  React.useEffect(() => {
    const onStorage = () => {
      setOrigUser(localStorage.getItem('originalUsername') || '');
      setOrigRoles(localStorage.getItem('originalRoles') || '');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Sync a `body.app-impersonating` class so global CSS can bump
  // `--app-header-offset` (drawers + overlays read it). Without this the
  // .fr-filter-panel / .fr-overlay sit at top:56 and cover the lower half of
  // the navbar when the impersonation banner pushes everything ~32px down.
  React.useEffect(() => {
    const active = !!(origUser && origRoles);
    document.body.classList.toggle('app-impersonating', active);
    return () => document.body.classList.remove('app-impersonating');
  }, [origUser, origRoles]);

  if (!origUser || !origRoles) return null;

  const onExit = async () => {
    try {
      const res = await fetch(process.env.REACT_APP_IP + '/Admin/ExitImpersonation', {
        method: 'POST', credentials: 'include',
      });
      const json = await res.json();
      if (json.type === 'OK') {
        localStorage.setItem('roles', json.role || origRoles);
        localStorage.setItem('uid',   String(json.id || ''));
        localStorage.removeItem('originalRoles');
        localStorage.removeItem('originalUid');
        localStorage.removeItem('originalUsername');
        window.location.href = '/';
      } else {
        // Cookie may have expired — fall back to a hard logout so the user re-auths.
        localStorage.clear();
        window.location.href = '/login';
      }
    } catch (err) {
      localStorage.clear();
      window.location.href = '/login';
    }
  };

  const currentUser = localStorage.getItem('uid') || '';
  const currentRole = (localStorage.getItem('roles') || '').replace('ROLE_', '');
  return (
    <div style={{
      // Sticky banner sits at z-index 1030 (Bootstrap's standard "sticky-
      // top" tier). MUST stay below the modal-backdrop (1050) and modal
      // (1055) layers — otherwise an open modal renders with its header
      // tucked under this strip and the close button becomes invisible.
      position: 'sticky', top: 0, zIndex: 1030,
      background: 'linear-gradient(90deg, #fef3c7 0%, #fde68a 100%)',
      borderBottom: '1px solid #f59e0b',
      padding: '6px 14px',
      fontSize: 12, fontWeight: 500, color: '#92400e',
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <span>👤 Viewing as <b>uid:{currentUser}</b> ({currentRole}) — impersonated by <b>{origUser}</b> ({origRoles.replace('ROLE_', '')})</span>
      <span style={{ flex: 1 }} />
      <button type="button"
        onClick={onExit}
        style={{
          background: '#92400e', color: '#fffbeb', border: 'none',
          borderRadius: 4, padding: '4px 12px', fontSize: 12, fontWeight: 600,
          cursor: 'pointer',
        }}>
        Exit impersonation
      </button>
    </div>
  );
}

function App() {

  return (
    <Router>
       <ImpersonationBanner />
       <AgentReconnectBanner />
       <CallbackAutoBanner />
       <SideBar>
        <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public — Dashboard / login landing (SideBar handles login check) */}
          <Route path="/" element={<Dashboard commonProp="Dashboard" />} />
          <Route path="/Dashboard" element={<Dashboard commonProp="Dashboard" />} />

          {/* ===== SUPERADMIN only ===== */}
          <Route path="/AdminPermissions" element={<ProtectedRoute roles={SUPER}><AdminPermissions /></ProtectedRoute>} />
          {/* Channel + DID assignment screens are shared with RESELLER —
              backend already gates per-subtree + quota, so the page itself
              can render for both roles. */}
          <Route path="/ChannelSetting" element={<ProtectedRoute roles={SUPER_RESELLER_ADMIN}><ChannelSetting /></ProtectedRoute>} />
          <Route path="/TrunkSettings" element={<ProtectedRoute roles={['ROLE_SUPERADMIN']}><TrunkSettings /></ProtectedRoute>} />
          <Route path="/AssignDid" element={<ProtectedRoute roles={SUPER_RESELLER}><AssignDid /></ProtectedRoute>} />
          <Route path="/PasswordChangeLogs" element={<ProtectedRoute roles={SUPER}><PasswordChangeLogs /></ProtectedRoute>} />
          <Route path="/SuperAdminActivityLog" element={<ProtectedRoute roles={SUPER_RESELLER}><SuperAdminActivityLog /></ProtectedRoute>} />
          <Route path="/JitsiDiagnostics" element={<ProtectedRoute roles={SUPER}><JitsiDiagnostics /></ProtectedRoute>} />
          <Route path="/JitsiSettings" element={<ProtectedRoute roles={['ROLE_SUPERADMIN', 'ROLE_ADMIN', 'ROLE_OBDADMIN', 'ROLE_TEAMLEADER']}><JitsiSettings /></ProtectedRoute>} />
          <Route path="/Video" element={<ProtectedRoute roles={ALL_ROLES}><VideoDashboard /></ProtectedRoute>} />
          <Route path="/Video/Dashboard" element={<ProtectedRoute roles={ALL_ROLES}><VideoDashboard /></ProtectedRoute>} />
          <Route path="/Video/NewMeeting" element={<ProtectedRoute roles={ALL_ROLES}><VideoNewMeeting /></ProtectedRoute>} />
          <Route path="/Video/MyRooms" element={<ProtectedRoute roles={ALL_ROLES}><VideoMyRooms /></ProtectedRoute>} />
          <Route path="/Video/Room/:name" element={<ProtectedRoute roles={ALL_ROLES}><VideoRoom /></ProtectedRoute>} />
          <Route path="/Video/Escalate" element={<ProtectedRoute roles={ALL_ROLES}><VideoEscalate /></ProtectedRoute>} />
          <Route path="/Video/LiveMonitor" element={<ProtectedRoute roles={['ROLE_SUPERADMIN', 'ROLE_ADMIN', 'ROLE_OBDADMIN', 'ROLE_TEAMLEADER']}><VideoLiveMonitor /></ProtectedRoute>} />
          <Route path="/Video/Recordings" element={<ProtectedRoute roles={ALL_ROLES}><VideoRecordings /></ProtectedRoute>} />
          <Route path="/LiveKit/Call" element={<ProtectedRoute roles={ALL_ROLES}><LiveKitCall /></ProtectedRoute>} />
          <Route path="/VoiceBotAccess" element={<ProtectedRoute roles={['ROLE_SUPERADMIN', 'ROLE_ADMIN', 'ROLE_OBDADMIN', 'ROLE_TEAMLEADER']}><VoiceBotAccess /></ProtectedRoute>} />
          <Route path="/ChannelStatus" element={<ProtectedRoute roles={['ROLE_SUPERADMIN', 'ROLE_ADMIN', 'ROLE_OBDADMIN', 'ROLE_TEAMLEADER', 'ROLE_RESELLER']}><ChannelStatus /></ProtectedRoute>} />
          <Route path="/MyChannelStatusAccess" element={<ProtectedRoute roles={['ROLE_RESELLER']}><MyChannelStatusAccess /></ProtectedRoute>} />
          <Route path="/JitsiWidgetSettings" element={<ProtectedRoute roles={['ROLE_SUPERADMIN', 'ROLE_ADMIN', 'ROLE_OBDADMIN']}><JitsiWidgetSettings /></ProtectedRoute>} />
          <Route path="/Resellers" element={<ProtectedRoute roles={SUPER}><Resellers /></ProtectedRoute>} />
          <Route path="/MyQuota" element={<ProtectedRoute roles={['ROLE_RESELLER']}><MyQuota /></ProtectedRoute>} />
          <Route path="/MyResellers" element={<ProtectedRoute roles={['ROLE_RESELLER']}><MyResellers /></ProtectedRoute>} />
          <Route path="/MyVoiceBotAccess" element={<ProtectedRoute roles={['ROLE_RESELLER']}><MyVoiceBotAccess /></ProtectedRoute>} />
          <Route path="/MyMessagingAccess" element={<ProtectedRoute roles={['ROLE_RESELLER']}><MyMessagingAccess /></ProtectedRoute>} />
          <Route path="/MyAdminPermissions" element={<ProtectedRoute roles={['ROLE_RESELLER']}><MyAdminPermissions /></ProtectedRoute>} />

          {/* ===== ADMIN + TEAMLEADER ===== */}
          <Route path="/Realtime" element={<ProtectedRoute roles={ADMIN_SUPER}><Realtime commonProp="Realtime" /></ProtectedRoute>} />
          <Route path="/Users" element={<ProtectedRoute roles={[...ADMIN_SUPER, 'ROLE_OBDADMIN', 'ROLE_RESELLER']}><Users /></ProtectedRoute>} />
          <Route path="/TeamLeaders" element={<ProtectedRoute roles={ADMIN}><TeamLeaders /></ProtectedRoute>} />

          {/* Campaign management */}
          <Route path="/AddCampaign" element={<ProtectedRoute roles={ADMIN}><AddCampaign /></ProtectedRoute>} />
          <Route path="/Makegui" element={<ProtectedRoute roles={ADMIN}><Makegui /></ProtectedRoute>} />
          <Route path="/Editgui" element={<ProtectedRoute roles={ADMIN}><Editgui /></ProtectedRoute>} />
          <Route path="/MusicOnHold" element={<ProtectedRoute roles={ADMIN}><MusicOnHold /></ProtectedRoute>} />
          <Route path="/Assignlist" element={<ProtectedRoute roles={ADMIN}><Assignlist /></ProtectedRoute>} />
          <Route path="/Assignagent" element={<ProtectedRoute roles={ADMIN}><Assignagent /></ProtectedRoute>} />
          <Route path="/CampDid" element={<ProtectedRoute roles={ADMIN}><CampDid /></ProtectedRoute>} />
          <Route path="/AddDisposition" element={<ProtectedRoute roles={ADMIN}><AddDisposition /></ProtectedRoute>} />
          <Route path="/CampaignScript" element={<ProtectedRoute roles={ADMIN}><CampaignScript /></ProtectedRoute>} />
          <Route path="/ChurnLeads" element={<ProtectedRoute roles={ADMIN}><ChurnLeads /></ProtectedRoute>} />

          {/* Voice-Bot */}
          <Route path="/VoiceBot" element={<ProtectedRoute roles={[...ADMIN, 'ROLE_OBDADMIN']}><VoiceBot /></ProtectedRoute>} />
          <Route path="/VoiceBotIntent" element={<ProtectedRoute roles={[...ADMIN, 'ROLE_OBDADMIN']}><VoiceBotIntent /></ProtectedRoute>} />
          <Route path="/VoiceBotKnowledgeBase" element={<ProtectedRoute roles={[...ADMIN, 'ROLE_OBDADMIN']}><VoiceBotKnowledgeBase /></ProtectedRoute>} />
          <Route path="/VoiceBotTools" element={<ProtectedRoute roles={[...ADMIN, 'ROLE_OBDADMIN']}><VoiceBotTools /></ProtectedRoute>} />
          <Route path="/VoiceBotCallLogs" element={<ProtectedRoute roles={[...ADMIN, 'ROLE_OBDADMIN']}><VoiceBotCallLogs /></ProtectedRoute>} />
          <Route path="/VoiceBotSettings" element={<ProtectedRoute roles={[...ADMIN, 'ROLE_OBDADMIN', 'ROLE_SUPERADMIN']}><VoiceBotSettings /></ProtectedRoute>} />
          <Route path="/MessagingSettings" element={<ProtectedRoute roles={[...ADMIN, 'ROLE_OBDADMIN', 'ROLE_SUPERADMIN']}><MessagingSettings /></ProtectedRoute>} />
          <Route path="/Messaging" element={<ProtectedRoute roles={[...ADMIN, 'ROLE_OBDADMIN']}><Messaging /></ProtectedRoute>} />
          <Route path="/VoiceBotConversions" element={<ProtectedRoute roles={[...ADMIN, 'ROLE_OBDADMIN']}><VoiceBotConversions /></ProtectedRoute>} />
          <Route path="/VoiceBotQuotas" element={<ProtectedRoute roles={[...ADMIN, 'ROLE_OBDADMIN', 'ROLE_SUPERADMIN']}><VoiceBotQuotas /></ProtectedRoute>} />
          <Route path="/Skills" element={<ProtectedRoute roles={ADMIN_SUPER}><Skills /></ProtectedRoute>} />
          <Route path="/SupervisorSkills" element={<ProtectedRoute roles={ADMIN_SUPER}><SupervisorSkills /></ProtectedRoute>} />
          <Route path="/GeminiVoiceCatalog" element={<ProtectedRoute roles={['ROLE_SUPERADMIN']}><GeminiVoiceCatalog /></ProtectedRoute>} />
          <Route path="/AngularBuildUpload" element={<ProtectedRoute roles={['ROLE_SUPERADMIN']}><AngularBuildUpload /></ProtectedRoute>} />

          {/* Leads */}

          {/* Reports */}
          <Route path="/Reportlive" element={<ProtectedRoute roles={[...ADMIN_SUPER, 'ROLE_RESELLER']}><Reportlive /></ProtectedRoute>} />
          <Route path="/Report" element={<ProtectedRoute roles={[...ADMIN_SUPER, 'ROLE_RESELLER']}><Report /></ProtectedRoute>} />
          <Route path="/FilterReport" element={<ProtectedRoute roles={ADMIN}><FilterReport /></ProtectedRoute>} />
          <Route path="/FilterReportold" element={<ProtectedRoute roles={ADMIN}><FilterReportold /></ProtectedRoute>} />
          <Route path="/Agentlog" element={<ProtectedRoute roles={[...ADMIN_SUPER, 'ROLE_RESELLER']}><Agentlog /></ProtectedRoute>} />
          <Route path="/AgentPerformance" element={<ProtectedRoute roles={ADMIN}><AgentPerformance /></ProtectedRoute>} />
          <Route path="/BreakReport" element={<ProtectedRoute roles={ADMIN}><BreakReport /></ProtectedRoute>} />
          <Route path="/Callbackreport" element={<ProtectedRoute roles={ADMIN}><Callbackreport /></ProtectedRoute>} />
          <Route path="/Callbackreportall" element={<ProtectedRoute roles={ADMIN}><Callbackreportall /></ProtectedRoute>} />

          {/* Settings — Admin */}
          <Route path="/UploadAudio" element={<ProtectedRoute roles={[...ADMIN_SUPER, 'ROLE_RESELLER']}><UploadAudio /></ProtectedRoute>} />
          <Route path="/UploadNumber" element={<ProtectedRoute roles={ADMIN_SUPER}><UploadNumber /></ProtectedRoute>} />
          <Route path="/BlockNumber" element={<ProtectedRoute roles={ADMIN}><BlockNumber /></ProtectedRoute>} />
          <Route path="/AllowIP" element={<ProtectedRoute roles={ADMIN_SUPER}><AllowIP /></ProtectedRoute>} />
          <Route path="/AllowPort" element={<ProtectedRoute roles={SUPER}><AllowPort /></ProtectedRoute>} />
          <Route path="/Fail2ban" element={<ProtectedRoute roles={SUPER}><Fail2ban /></ProtectedRoute>} />
          <Route path="/NetworkMonitor" element={<ProtectedRoute roles={SUPER}><NetworkMonitor /></ProtectedRoute>} />
          <Route path="/DncSettings" element={<ProtectedRoute roles={SUPER}><DncSettings /></ProtectedRoute>} />
          <Route path="/PjsipEndpoints" element={<ProtectedRoute roles={ADMIN_SUPER}><PjsipEndpoints /></ProtectedRoute>} />
          {/* OBDUSER may also reach these when their parent OBDADMIN has
              flipped the matching self-serve flag on the User row. The
              backend gate (canActOnObdResources) is authoritative — the
              route just stops blocking the page from rendering at all. */}
          <Route path="/AddObdCampaign" element={<ProtectedRoute roles={['ROLE_OBDADMIN', 'ROLE_OBDUSER']}><AddObdCampaign /></ProtectedRoute>} />
          <Route path="/ObdRecording" element={<ProtectedRoute roles={['ROLE_OBDADMIN', 'ROLE_OBDUSER']}><ObdRecording /></ProtectedRoute>} />
          <Route path="/UploadObdNumber" element={<ProtectedRoute roles={['ROLE_OBDADMIN', 'ROLE_OBDUSER']}><UploadObdNumber /></ProtectedRoute>} />
          <Route path="/AddObdAgent" element={<ProtectedRoute roles={['ROLE_OBDADMIN', 'ROLE_OBDUSER']}><AddObdAgent /></ProtectedRoute>} />
          <Route path="/ObdChannelSetting" element={<ProtectedRoute roles={['ROLE_OBDADMIN']}><ObdChannelSetting /></ProtectedRoute>} />
          <Route path="/ObdBlockNumber" element={<ProtectedRoute roles={['ROLE_OBDADMIN', 'ROLE_OBDUSER']}><ObdBlockNumber /></ProtectedRoute>} />
          <Route path="/ObdProfile" element={<ProtectedRoute roles={['ROLE_OBDADMIN', 'ROLE_OBDUSER']}><ObdProfile /></ProtectedRoute>} />
          <Route path="/ObdReport" element={<ProtectedRoute roles={['ROLE_OBDADMIN', 'ROLE_OBDUSER']}><ObdReport /></ProtectedRoute>} />
          <Route path="/ObdUserPermissions" element={<ProtectedRoute roles={['ROLE_OBDADMIN']}><ObdUserPermissions /></ProtectedRoute>} />
          <Route path="/ObdDialerSettings" element={<ProtectedRoute roles={SUPER}><ObdDialerSettings /></ProtectedRoute>} />
          <Route path="/DialerSettings" element={<ProtectedRoute roles={SUPER}><DialerSettings /></ProtectedRoute>} />
          <Route path="/ObdFlowList" element={<ProtectedRoute roles={['ROLE_OBDADMIN', 'ROLE_OBDUSER']}><ObdFlowList /></ProtectedRoute>} />
          <Route path="/DrawObdFlow/:flowId" element={<ProtectedRoute roles={['ROLE_OBDADMIN', 'ROLE_OBDUSER']}><DrawObdFlow /></ProtectedRoute>} />
          <Route path="/Break" element={<ProtectedRoute roles={ADMIN}><Break /></ProtectedRoute>} />
          <Route path="/TransferNumber" element={<ProtectedRoute roles={ADMIN}><TransferNumber /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute roles={[...ADMIN_SUPER, 'ROLE_RESELLER']}><Setting /></ProtectedRoute>} />

          {/* IVR */}
          <Route path="/AddAgent" element={<ProtectedRoute roles={ADMIN}><AddAgent /></ProtectedRoute>} />
          <Route path="/DrawIvr" element={<ProtectedRoute roles={ADMIN}><DrawIvr /></ProtectedRoute>} />
          <Route path="/IvrTab" element={<ProtectedRoute roles={ADMIN}><IvrTab /></ProtectedRoute>} />
          <Route path="/IvrSmsReport" element={<ProtectedRoute roles={ADMIN}><IvrSmsReport /></ProtectedRoute>} />
          <Route path="/AddIvr" element={<ProtectedRoute roles={ADMIN}><Ivr /></ProtectedRoute>} />
          <Route path="/Audio" element={<ProtectedRoute roles={ADMIN}><Audio /></ProtectedRoute>} />
          <Route path="/Group" element={<ProtectedRoute roles={ADMIN}><Group /></ProtectedRoute>} />
          <Route path="/IvrGroupAssignAgent" element={<ProtectedRoute roles={ADMIN}><IvrGroupAssignAgent /></ProtectedRoute>} />
          <Route path="/Agent" element={<ProtectedRoute roles={ADMIN}><Agent /></ProtectedRoute>} />

          {/* SMS */}
          <Route path="/Smssuits" element={<ProtectedRoute roles={ADMIN}><Smssuits /></ProtectedRoute>} />
          <Route path="/SmsportSetting" element={<ProtectedRoute roles={ADMIN_SUPER}><SmsportSetting /></ProtectedRoute>} />

          {/* ===== AGENT / USER pages ===== */}
          <Route path="/Crm" element={<ProtectedRoute roles={[...AGENT_USER, ...ADMIN]}><Crm /></ProtectedRoute>} />
          <Route path="/Callback" element={<ProtectedRoute roles={AGENT_USER}><Callback /></ProtectedRoute>} />
          <Route path="/PreviewDialer" element={<ProtectedRoute roles={AGENT_USER}><PreviewDialer /></ProtectedRoute>} />
          <Route path="/Callbackactive" element={<ProtectedRoute roles={ALL_ROLES}><Callbackactive /></ProtectedRoute>} />
          <Route path="/Callbackall" element={<ProtectedRoute roles={[...AGENT_USER, ...ADMIN]}><Callbackall /></ProtectedRoute>} />
          <Route path="/Agentcalllog" element={<ProtectedRoute roles={[...AGENT_USER, 'ROLE_AGENT']}><Agentcalllog /></ProtectedRoute>} />
          <Route path="/AgentApr" element={<ProtectedRoute roles={[...AGENT_USER, 'ROLE_AGENT']}><AgentApr /></ProtectedRoute>} />
          <Route path="/Callhistory" element={<ProtectedRoute roles={[...AGENT_USER, 'ROLE_AGENT']}><Callhistory /></ProtectedRoute>} />
          <Route path="/CallLog" element={<ProtectedRoute roles={['ROLE_AGENT', ...AGENT_USER]}><Calllog /></ProtectedRoute>} />
          <Route path="/IvrReport" element={<ProtectedRoute roles={['ROLE_AGENT', ...AGENT_USER, ...ADMIN]}><IvrReport /></ProtectedRoute>} />

          {/* Shared — any logged-in user */}
          <Route path="/Changepwd" element={<ProtectedRoute roles={ALL_ROLES}><Changepwd /></ProtectedRoute>} />
          <Route path="/Sharescreen" element={<ProtectedRoute roles={ALL_ROLES}><Sharescreen /></ProtectedRoute>} />

          {/* Misc */}
          <Route path="/messages" element={<ProtectedRoute roles={ALL_ROLES}><Messages /></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute roles={ALL_ROLES}><Analytics /></ProtectedRoute>} />
          <Route path="/file-manager" element={<ProtectedRoute roles={ALL_ROLES}><FileManager /></ProtectedRoute>} />
          <Route path="/order" element={<ProtectedRoute roles={ALL_ROLES}><Order /></ProtectedRoute>} />
          <Route path="/saved" element={<ProtectedRoute roles={ALL_ROLES}><Saved /></ProtectedRoute>} />

          {/* Catch-all */}
          <Route path="*" element={<></>} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
       </SideBar>
    </Router>
  );
}

export default App;
